import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { Paintbrush, Layout, Check, Sun, Moon, Plus, Pencil, Trash2, Video, X, Upload, Film, Play, Copy } from 'lucide-react';
import { deleteCustomColorScheme, deleteCustomStyle } from '@/theme/customThemes';
import themeConfig from '@/theme/theme.config';
import { deletePersonalTheme, uploadThemeVideo, listThemeVideos, deleteThemeVideo, saveThemePreference } from '@/api/themeApi';
import ColourSchemeEditor from './ColourSchemeEditor';
import LayoutStyleEditor from './LayoutStyleEditor';

/* ── Small preview card used inside each option ── */
const MiniPreview = ({ colors, radii, label }) => {
  const rad = radii === 'rounded' ? '0.5rem' : '0.25rem';
  const cardRad = radii === 'rounded' ? '1rem' : '0.375rem';
  return (
    <div
      className="w-full h-28 p-2.5 flex flex-col gap-1.5 overflow-hidden"
      style={{ background: colors.body, borderRadius: cardRad }}
    >
      {/* Top bar */}
      <div className="flex items-center gap-1.5">
        <div className="w-5 h-2.5" style={{ background: colors.sidebar, borderRadius: rad }} />
        <div className="flex-1 h-2.5" style={{ background: colors.surface, borderRadius: rad, border: `1px solid ${colors.border}` }} />
      </div>
      {/* Cards row */}
      <div className="flex gap-1.5 flex-1">
        <div className="flex-1 flex flex-col gap-1 p-1.5" style={{ background: colors.surface, borderRadius: rad, border: `1px solid ${colors.border}` }}>
          <div className="w-full h-1.5" style={{ background: colors.primary, borderRadius: '2px' }} />
          <div className="w-3/4 h-1" style={{ background: colors.textSec, borderRadius: '2px', opacity: 0.4 }} />
          <div className="mt-auto flex gap-1">
            <div className="w-6 h-2.5" style={{ background: colors.primary, borderRadius: rad }} />
            <div className="w-6 h-2.5" style={{ background: colors.border, borderRadius: rad }} />
          </div>
        </div>
        <div className="flex-1 p-1.5" style={{ background: colors.surface, borderRadius: rad, border: `1px solid ${colors.border}` }}>
          <div className="w-full h-1.5 mb-1" style={{ background: colors.text, borderRadius: '2px', opacity: 0.3 }} />
          <div className="w-2/3 h-1" style={{ background: colors.textSec, borderRadius: '2px', opacity: 0.3 }} />
          <div className="mt-2 w-full h-4" style={{ background: colors.input, borderRadius: rad, border: `1px solid ${colors.border}` }} />
        </div>
      </div>
      {/* Label inside preview */}
      <div className="text-center" style={{ color: colors.text, fontSize: '0.55rem', fontWeight: 600, letterSpacing: '0.02em' }}>
        {label}
      </div>
    </div>
  );
};

/* ── Colour scheme preview colours: built-in → hardcoded, custom → derived from .colors ── */
const BUILTIN_COLOR_PREVIEWS = {
  light: {
    body: '#f8fafc', surface: '#ffffff', primary: '#2563eb', sidebar: '#93c5fd',
    text: '#1e293b', textSec: '#6b7280', border: '#e5e7eb', input: '#f1f5f9',
  },
  dark: {
    body: '#111827', surface: '#1f2937', primary: '#3b82f6', sidebar: '#60a5fa',
    text: '#f3f4f6', textSec: '#9ca3af', border: '#374151', input: '#111827',
  },
  'ai-light': {
    body: '#f5f5f5', surface: '#ffffff', primary: '#1976d2', sidebar: '#1976d2',
    text: '#212121', textSec: '#757575', border: '#e0e0e0', input: '#eeeeee',
  },
  'ai-dark': {
    body: '#121212', surface: '#1e1e1e', primary: '#90caf9', sidebar: '#1e1e1e',
    text: '#ffffff', textSec: 'rgba(255,255,255,0.5)', border: '#333333', input: '#121212',
  },
  'command-center': {
    body: '#0a0e17', surface: '#111827', primary: '#00ff88', sidebar: '#0f172a',
    text: '#e0f2fe', textSec: '#64748b', border: '#1e293b', input: '#0f172a',
  },
  'aurora': {
    body: '#0f0b1e', surface: '#1a1333', primary: '#a855f7', sidebar: '#150e2b',
    text: '#f0e7ff', textSec: '#a78bfa', border: '#2e1f5e', input: '#150e2b',
  },
  'stealth': {
    body: '#09090b', surface: '#18181b', primary: '#f59e0b', sidebar: '#09090b',
    text: '#fafafa', textSec: '#a1a1aa', border: '#27272a', input: '#09090b',
  },
};

function getColorPreview(cs) {
  if (BUILTIN_COLOR_PREVIEWS[cs.id]) return BUILTIN_COLOR_PREVIEWS[cs.id];
  // Custom scheme — map from its colors object
  const c = cs.colors || {};
  return {
    body: c.backgroundBody || '#f8fafc',
    surface: c.backgroundSurface || '#ffffff',
    primary: c.primary || '#2563eb',
    sidebar: c.sidebar || '#93c5fd',
    text: c.textPrimary || '#1e293b',
    textSec: c.textSecondary || '#6b7280',
    border: c.borderDefault || '#e5e7eb',
    input: c.backgroundInput || '#f1f5f9',
  };
}

/* ── Layout style preview overrides ── */
const STYLE_RADII = {
  standard: 'rounded',
  ai: 'angular',
  'command-center': 'angular',
  aurora: 'rounded',
  stealth: 'angular',
};

/* ── Style tags for built-in styles ── */
const STYLE_TAGS = {
  standard: ['Rounded corners', 'Soft shadows', 'Arial font', 'Bordered cards'],
  ai: ['Angular corners', 'MUI shadows', 'Roboto font', 'Borderless cards', 'Uppercase buttons'],
  'command-center': ['Zero-radius', 'Hard shadows', 'Monospace font', 'Uppercase', 'Compact'],
  aurora: ['Pill shapes', 'Glow shadows', 'Inter font', 'Spacious padding'],
  stealth: ['Subtle radii', 'Flat shadows', 'System font', 'Uppercase', 'Minimal'],
};


/* ── Background Video section (with upload) ── */
const BackgroundVideoSection = ({ backgroundVideo, setBackgroundVideo }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [videos, setVideos] = useState([]);
  const [showLibrary, setShowLibrary] = useState(false);

  // Fetch all videos (built-in + user uploads) from backend
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      listThemeVideos()
        .then((list) => setVideos(list))
        .catch(() => setVideos([]));
    }
  }, []);

  // Refresh videos list after successful upload
  const refreshVideos = () => {
    const token = localStorage.getItem('token');
    if (token) {
      listThemeVideos()
        .then((list) => setVideos(list))
        .catch(() => {});
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so the same file can be re-selected
    e.target.value = '';

    const allowedTypes = ['video/mp4', 'video/webm', 'video/ogg'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Only .mp4, .webm, and .ogg files are supported.');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setUploadError('File too large — max 50 MB.');
      return;
    }

    setUploadError('');
    setUploading(true);
    setUploadProgress(0);
    try {
      const result = await uploadThemeVideo(file, (pct) => setUploadProgress(pct));
      setBackgroundVideo({ ...backgroundVideo, url: result.url, overlayOpacity: backgroundVideo?.overlayOpacity ?? 0.7 });
      refreshVideos();
    } catch (err) {
      setUploadError(err?.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mt-8">
      <div className="flex items-center gap-2 mb-3">
        <Video className="w-4 h-4 text-textSecondary" />
        <h3 className="text-sm font-semibold text-textPrimary uppercase tracking-wider">Background Video</h3>
      </div>
      <p className="text-xs text-textSecondary mb-4">
        Upload or link a looping video as the platform background. The current theme colour overlays the video for readability.
      </p>
      <div className="p-4 rounded-lg bg-backgroundSurface border border-borderDefault space-y-4">
        {/* Upload + URL row */}
        <div>
          <label className="block text-xs font-medium text-textSecondary mb-1">Video Source</label>
          <div className="flex gap-2 flex-wrap">
            {/* Upload button */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm bg-primary text-textAccent hover:bg-primaryHover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="w-4 h-4" />
              {uploading ? `Uploading ${uploadProgress}%` : 'Upload Video'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/webm,video/ogg"
              className="hidden"
              onChange={handleFileSelect}
            />
            {/* URL input */}
            <input
              type="text"
              value={backgroundVideo?.url || ''}
              onChange={(e) => setBackgroundVideo(e.target.value ? { ...backgroundVideo, url: e.target.value, overlayOpacity: backgroundVideo?.overlayOpacity ?? 0.7 } : null)}
              placeholder="…or paste a URL"
              className="flex-1 min-w-[200px] px-3 py-2 rounded-md bg-backgroundInput border border-borderDefault text-textPrimary text-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
            />
            {backgroundVideo?.url && (
              <button
                onClick={() => setBackgroundVideo(null)}
                className="px-3 py-2 rounded-md text-sm border border-borderDefault text-destructive hover:bg-destructive/10 transition-colors"
                title="Remove video"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {/* Progress bar */}
          {uploading && (
            <div className="mt-2 h-1.5 bg-backgroundInput rounded-full overflow-hidden max-w-xs">
              <div className="h-full bg-primary transition-all duration-200" style={{ width: `${uploadProgress}%` }} />
            </div>
          )}
          {uploadError && <p className="text-xs text-destructive mt-1">{uploadError}</p>}
          <p className="text-[10px] text-textSecondary mt-1">Accepts .mp4, .webm — max 50 MB. Use a short, subtle loop for best results.</p>
        </div>

        {/* Video Library */}
        <div>
          <button
            type="button"
            onClick={() => setShowLibrary((v) => !v)}
            className="inline-flex items-center gap-2 text-xs font-medium text-primary hover:text-primaryHover transition-colors mb-2"
          >
            <Film className="w-3.5 h-3.5" />
            {showLibrary ? 'Hide Video Library' : 'Browse Video Library'}
          </button>

          {showLibrary && (
            <div className="space-y-3">
              {/* Built-in videos */}
              {videos.filter((v) => v.is_builtin).length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-textSecondary uppercase tracking-wider mb-2">Built-in Videos</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {videos.filter((v) => v.is_builtin).map((v) => {
                      const isActive = backgroundVideo?.url === v.url;
                      return (
                        <button
                          key={v.id}
                          type="button"
                          onClick={() => setBackgroundVideo({ ...backgroundVideo, url: v.url, overlayOpacity: backgroundVideo?.overlayOpacity ?? 0.7 })}
                          className={`relative group rounded-lg overflow-hidden border-2 transition-all h-20 ${
                            isActive ? 'border-primary ring-2 ring-primary/30' : 'border-borderDefault hover:border-primary/50'
                          }`}
                        >
                          <video
                            src={v.url}
                            className="w-full h-full object-cover"
                            muted
                            playsInline
                            preload="metadata"
                            onMouseEnter={(e) => e.target.play().catch(() => {})}
                            onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                          />
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Play className="w-5 h-5 text-white" />
                          </div>
                          <div className="absolute bottom-0 inset-x-0 bg-black/60 px-1.5 py-0.5">
                            <span className="text-[10px] text-white truncate block">{v.original_name}</span>
                          </div>
                          {isActive && (
                            <div className="absolute top-1 right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                              <Check className="w-2.5 h-2.5 text-white" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* User-uploaded videos */}
              {videos.filter((v) => !v.is_builtin).length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-textSecondary uppercase tracking-wider mb-2">Your Uploads</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {videos.filter((v) => !v.is_builtin).map((v) => {
                      const isActive = backgroundVideo?.url === v.url;
                      return (
                        <div key={v.id || v.url} className="relative group">
                          <button
                            type="button"
                            onClick={() => setBackgroundVideo({ ...backgroundVideo, url: v.url, overlayOpacity: backgroundVideo?.overlayOpacity ?? 0.7 })}
                            className={`relative w-full rounded-lg overflow-hidden border-2 transition-all h-20 ${
                              isActive ? 'border-primary ring-2 ring-primary/30' : 'border-borderDefault hover:border-primary/50'
                            }`}
                          >
                            <video
                              src={v.url}
                              className="w-full h-full object-cover"
                              muted
                              playsInline
                              preload="metadata"
                              onMouseEnter={(e) => e.target.play().catch(() => {})}
                              onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                            />
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <Play className="w-5 h-5 text-white" />
                            </div>
                            <div className="absolute bottom-0 inset-x-0 bg-black/60 px-1.5 py-0.5">
                              <span className="text-[10px] text-white truncate block">{v.original_name || 'Uploaded video'}</span>
                            </div>
                            {isActive && (
                              <div className="absolute top-1 right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                                <Check className="w-2.5 h-2.5 text-white" />
                              </div>
                            )}
                          </button>
                          {/* Delete button */}
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!confirm('Delete this uploaded video?')) return;
                              try {
                                await deleteThemeVideo(v.id);
                                // If the deleted video is currently active, clear it
                                if (backgroundVideo?.url === v.url) setBackgroundVideo(null);
                                refreshVideos();
                              } catch {
                                setUploadError('Failed to delete video.');
                              }
                            }}
                            className="absolute top-1 left-1 w-5 h-5 bg-black/70 hover:bg-destructive rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                            title="Delete video"
                          >
                            <Trash2 className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {videos.length === 0 && (
                <p className="text-xs text-textSecondary italic">No videos available. Upload one above.</p>
              )}
            </div>
          )}
        </div>

        {/* Overlay slider */}
        <div>
          <label className="block text-xs font-medium text-textSecondary mb-1">Video Overlay Opacity: {Math.round((backgroundVideo?.overlayOpacity ?? 0.7) * 100)}%</label>
          <input
            type="range"
            min="0"
            max="100"
            value={Math.round((backgroundVideo?.overlayOpacity ?? 0.7) * 100)}
            onChange={(e) => setBackgroundVideo({ ...backgroundVideo, url: backgroundVideo?.url || '', overlayOpacity: Number(e.target.value) / 100 })}
            className="w-full max-w-xs accent-primary"
          />
          <div className="flex justify-between text-[10px] text-textSecondary max-w-xs">
            <span>0% (full video)</span>
            <span>100% (hidden)</span>
          </div>
        </div>

        {/* Content background opacity slider */}
        <div>
          <label className="block text-xs font-medium text-textSecondary mb-1">Content Background Opacity: {Math.round((backgroundVideo?.contentBgOpacity ?? 0.85) * 100)}%</label>
          <input
            type="range"
            min="0"
            max="100"
            value={Math.round((backgroundVideo?.contentBgOpacity ?? 0.85) * 100)}
            onChange={(e) => setBackgroundVideo({ ...backgroundVideo, url: backgroundVideo?.url || '', contentBgOpacity: Number(e.target.value) / 100 })}
            className="w-full max-w-xs accent-primary"
          />
          <div className="flex justify-between text-[10px] text-textSecondary max-w-xs">
            <span>0% (fully transparent)</span>
            <span>100% (solid)</span>
          </div>
          <p className="text-[10px] text-textSecondary mt-1">Controls how transparent page backgrounds (dashboard cards, tables, etc.) are so the video shows through.</p>
        </div>

        {/* Preview */}
        {backgroundVideo?.url && (
          <div className="rounded-lg overflow-hidden border border-borderDefault h-32 relative">
            <video
              src={backgroundVideo.url}
              className="w-full h-full object-cover"
              autoPlay loop muted playsInline
            />
            <div className="absolute inset-0 bg-backgroundBody" style={{ opacity: backgroundVideo.overlayOpacity ?? 0.7 }} />
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="px-4 py-2 rounded-lg"
                style={{
                  backgroundColor: `color-mix(in srgb, var(--color-background-surface) ${Math.round((backgroundVideo.contentBgOpacity ?? 0.85) * 100)}%, transparent)`,
                }}
              >
                <span className="text-textPrimary text-xs font-medium">Content Preview</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


const ThemeBuilder = () => {
  const { style, setStyle, styles, colorScheme, setColorScheme, colorSchemes, refreshCustomThemes, backgroundVideo, setBackgroundVideo } = useTheme();

  // ── Direct save helper — ensures preference is persisted to DB ──
  const saveTimerRef = useRef(null);
  const latestRef = useRef({ style, colorScheme, backgroundVideo });
  latestRef.current = { style, colorScheme, backgroundVideo };

  const persistPreference = (overrides = {}) => {
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const token = localStorage.getItem('token');
      if (!token) return;
      const cur = { ...latestRef.current, ...overrides };
      console.log('[ThemeBuilder] Persisting preference →', cur);
      saveThemePreference({
        activeStyle: cur.style,
        activeColorScheme: cur.colorScheme,
        backgroundVideoUrl: cur.backgroundVideo?.url || '',
        backgroundVideoOverlay: cur.backgroundVideo?.overlayOpacity ?? 0.7,
        contentBgOpacity: cur.backgroundVideo?.contentBgOpacity ?? 0.85,
      });
    }, 400);
  };

  // Editor state: { type: 'color'|'style', existing?: object } or null
  const [editor, setEditor] = useState(null);

  const handleDeleteColor = (id) => {
    if (!confirm('Delete this custom colour scheme?')) return;
    if (colorScheme === id) {
      setColorScheme('light');
      persistPreference({ colorScheme: 'light' });
    }
    deleteCustomColorScheme(id);
    const token = localStorage.getItem('token');
    if (token) deletePersonalTheme(id).catch(() => {});
    refreshCustomThemes();
  };

  const handleDeleteStyle = (id) => {
    if (!confirm('Delete this custom layout style?')) return;
    if (style === id) {
      setStyle('standard');
      persistPreference({ style: 'standard' });
    }
    deleteCustomStyle(id);
    const token = localStorage.getItem('token');
    if (token) deletePersonalTheme(id).catch(() => {});
    refreshCustomThemes();
  };

  const handleDuplicateColor = (cs) => {
    // Resolve actual palette: custom themes carry .colors, built-ins use themeConfig.colors[id]
    const resolvedColors = cs.colors || themeConfig.colors[cs.id] || {};
    const { id, custom, ...rest } = cs;
    setEditor({ type: 'color', existing: { ...rest, label: `${rest.label} (Copy)`, colors: resolvedColors } });
  };

  const handleDuplicateStyle = (s) => {
    // Custom styles carry .vars, built-ins use defaults (editor will fill DEFAULT_LAYOUT_STYLE)
    const { id, custom, ...rest } = s;
    setEditor({ type: 'style', existing: { ...rest, label: `${rest.label} (Copy)` } });
  };

  /* ── If an editor is open, show it full-width ── */
  if (editor) {
    if (editor.type === 'color') {
      return (
        <section>
          <ColourSchemeEditor
            existing={editor.existing || null}
            onClose={() => setEditor(null)}
          />
        </section>
      );
    }
    return (
      <section>
        <LayoutStyleEditor
          existing={editor.existing || null}
          onClose={() => setEditor(null)}
        />
      </section>
    );
  }

  return (
    <section>
      {/* Section heading */}
      <div className="flex items-center gap-2.5 mb-5">
        <Paintbrush className="w-5 h-5 text-primary" />
        <h2 className="text-lg sm:text-xl font-semibold text-textPrimary">Theme Builder</h2>
      </div>
      <p className="text-sm text-textSecondary mb-6">
        Mix and match any layout style with any colour scheme. Changes apply instantly across the entire platform.
      </p>

      {/* ── Layout Style ── */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <Layout className="w-4 h-4 text-textSecondary" />
          <h3 className="text-sm font-semibold text-textPrimary uppercase tracking-wider">Layout Style</h3>
        </div>
        <p className="text-xs text-textSecondary mb-4">
          Controls corner radius, shadows, typography, button style, card borders, and spacing.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {styles.map((s) => {
            const isActive = s.id === style;
            const previewColors = getColorPreview(colorSchemes.find((c) => c.id === colorScheme) || colorSchemes[0]);
            return (
              <button
                key={s.id}
                onClick={() => { setStyle(s.id); persistPreference({ style: s.id }); }}
                className={`relative group text-left rounded-xl border-2 overflow-hidden transition-all
                  ${isActive
                    ? 'border-primary shadow-md ring-2 ring-primary/20'
                    : 'border-borderDefault hover:border-primary/40 hover:shadow-sm'
                  }`}
              >
                {/* Active badge */}
                {isActive && (
                  <div className="absolute top-2 right-2 z-10 bg-primary text-textAccent rounded-full p-0.5">
                    <Check className="w-3 h-3" />
                  </div>
                )}
                {/* Action buttons — Duplicate for all, Edit/Delete for custom */}
                <div className="absolute top-2 left-2 z-10 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {s.custom && (
                    <span
                      onClick={(e) => { e.stopPropagation(); setEditor({ type: 'style', existing: s }); }}
                      className="bg-backgroundSurface/90 border border-borderDefault rounded p-1 hover:bg-primary/10 cursor-pointer"
                      title="Edit"
                    >
                      <Pencil className="w-3 h-3 text-textPrimary" />
                    </span>
                  )}
                  <span
                    onClick={(e) => { e.stopPropagation(); handleDuplicateStyle(s); }}
                    className="bg-backgroundSurface/90 border border-borderDefault rounded p-1 hover:bg-primary/10 cursor-pointer"
                    title="Duplicate"
                  >
                    <Copy className="w-3 h-3 text-textPrimary" />
                  </span>
                  {s.custom && (
                    <span
                      onClick={(e) => { e.stopPropagation(); handleDeleteStyle(s.id); }}
                      className="bg-backgroundSurface/90 border border-borderDefault rounded p-1 hover:bg-destructive/10 cursor-pointer"
                      title="Delete"
                    >
                      <Trash2 className="w-3 h-3 text-destructive" />
                    </span>
                  )}
                </div>
                <MiniPreview
                  colors={previewColors}
                  radii={STYLE_RADII[s.id] || (s.custom ? 'rounded' : 'rounded')}
                  label={s.label}
                />
                <div className="p-3 bg-backgroundSurface border-t border-borderDefault">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{s.icon}</span>
                    <span className="font-medium text-sm text-textPrimary">{s.label}</span>
                    {s.custom && (
                      <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded-full bg-accent/10 text-accent font-semibold">CUSTOM</span>
                    )}
                  </div>
                  <p className="text-xs text-textSecondary mt-1">{s.description || ''}</p>
                  {STYLE_TAGS[s.id] && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {STYLE_TAGS[s.id].map((tag) => (
                        <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              </button>
            );
          })}

          {/* ── + Create New Style card ── */}
          <button
            onClick={() => setEditor({ type: 'style' })}
            className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-borderDefault hover:border-primary/40 hover:bg-primary/5 transition-all min-h-[12rem] cursor-pointer group"
          >
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
              <Plus className="w-5 h-5 text-primary" />
            </div>
            <span className="text-sm font-medium text-textSecondary group-hover:text-primary transition-colors">Create Layout Style</span>
          </button>
        </div>
      </div>

      {/* ── Colour Scheme ── */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Paintbrush className="w-4 h-4 text-textSecondary" />
          <h3 className="text-sm font-semibold text-textPrimary uppercase tracking-wider">Colour Scheme</h3>
        </div>
        <p className="text-xs text-textSecondary mb-4">
          Sets the colour palette — backgrounds, text, accent colours, sidebar, and status colours.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {colorSchemes.map((cs) => {
            const isActive = cs.id === colorScheme;
            const previewColors = getColorPreview(cs);
            return (
              <button
                key={cs.id}
                onClick={() => { setColorScheme(cs.id); persistPreference({ colorScheme: cs.id }); }}
                className={`relative group text-left rounded-xl border-2 overflow-hidden transition-all
                  ${isActive
                    ? 'border-primary shadow-md ring-2 ring-primary/20'
                    : 'border-borderDefault hover:border-primary/40 hover:shadow-sm'
                  }`}
              >
                {isActive && (
                  <div className="absolute top-2 right-2 z-10 bg-primary text-textAccent rounded-full p-0.5">
                    <Check className="w-3 h-3" />
                  </div>
                )}
                {/* Action buttons — Duplicate for all, Edit/Delete for custom */}
                <div className="absolute top-2 left-2 z-10 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {cs.custom && (
                    <span
                      onClick={(e) => { e.stopPropagation(); setEditor({ type: 'color', existing: cs }); }}
                      className="bg-backgroundSurface/90 border border-borderDefault rounded p-1 hover:bg-primary/10 cursor-pointer"
                      title="Edit"
                    >
                      <Pencil className="w-3 h-3 text-textPrimary" />
                    </span>
                  )}
                  <span
                    onClick={(e) => { e.stopPropagation(); handleDuplicateColor(cs); }}
                    className="bg-backgroundSurface/90 border border-borderDefault rounded p-1 hover:bg-primary/10 cursor-pointer"
                    title="Duplicate"
                  >
                    <Copy className="w-3 h-3 text-textPrimary" />
                  </span>
                  {cs.custom && (
                    <span
                      onClick={(e) => { e.stopPropagation(); handleDeleteColor(cs.id); }}
                      className="bg-backgroundSurface/90 border border-borderDefault rounded p-1 hover:bg-destructive/10 cursor-pointer"
                      title="Delete"
                    >
                      <Trash2 className="w-3 h-3 text-destructive" />
                    </span>
                  )}
                </div>
                <MiniPreview
                  colors={previewColors}
                  radii={STYLE_RADII[style] || 'rounded'}
                  label={cs.label}
                />
                <div className="p-3 bg-backgroundSurface border-t border-borderDefault">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{cs.icon}</span>
                    <span className="font-medium text-sm text-textPrimary">{cs.label}</span>
                    {cs.isDark ? (
                      <Moon className="w-3 h-3 text-textSecondary ml-auto" />
                    ) : (
                      <Sun className="w-3 h-3 text-textSecondary ml-auto" />
                    )}
                  </div>
                  <div className="flex items-center gap-1 mt-2">
                    {/* Colour swatches */}
                    {[previewColors.primary, previewColors.sidebar, previewColors.body, previewColors.surface, previewColors.text].map((c, i) => (
                      <div
                        key={i}
                        className="w-5 h-5 rounded-full border border-borderDefault"
                        style={{ background: c }}
                        title={['Primary', 'Sidebar', 'Body', 'Surface', 'Text'][i]}
                      />
                    ))}
                    {cs.custom && (
                      <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded-full bg-accent/10 text-accent font-semibold">CUSTOM</span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}

          {/* ── + Create New Colour Scheme card ── */}
          <button
            onClick={() => setEditor({ type: 'color' })}
            className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-borderDefault hover:border-primary/40 hover:bg-primary/5 transition-all min-h-[12rem] cursor-pointer group"
          >
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
              <Plus className="w-5 h-5 text-primary" />
            </div>
            <span className="text-sm font-medium text-textSecondary group-hover:text-primary transition-colors">Create Colour Scheme</span>
          </button>
        </div>
      </div>

      {/* ── Background Video ── */}
      <BackgroundVideoSection backgroundVideo={backgroundVideo} setBackgroundVideo={setBackgroundVideo} />

      {/* ── Current combo summary ── */}
      <div className="mt-8 p-4 rounded-lg bg-backgroundSurface border border-borderDefault">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-textSecondary mb-2">
          Active Configuration
        </h4>
        <div className="flex flex-wrap items-center gap-3 text-sm text-textPrimary">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary font-medium">
            <Layout className="w-3.5 h-3.5" />
            {styles.find((s) => s.id === style)?.label || style}
          </span>
          <span className="text-textSecondary">+</span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary font-medium">
            <Paintbrush className="w-3.5 h-3.5" />
            {colorSchemes.find((c) => c.id === colorScheme)?.label || colorScheme}
          </span>
        </div>
      </div>
    </section>
  );
};

export default ThemeBuilder;
